import { useEffect, useContext, useState, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { AgencyContext } from 'Context/Agency'
import { Decrypt_Id_Name } from '@/Components/Common/Utility'
import { useDispatch, useSelector } from 'react-redux'
import { get_LocalStoreData } from '@/redux/actions/Agency'
import { Link } from 'react-router-dom'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHome, faUsers, faShieldAlt, faLock, faSitemap, faComments, faUserShield, faAddressCard, faGraduationCap, faCertificate, faHistory, faBell, faFileContract, faFileInvoice, faCog, faUser } from "@fortawesome/free-solid-svg-icons";
import ReportWorkflowList from '@/CADComponents/AgencyComponents/ReportWorkflowList'

const AgencySidebar = () => {

    const { getAgency, agencyFilterData, count, countoffaduitAgency } = useContext(AgencyContext);
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()
    const uniqueId = sessionStorage.getItem('UniqueUserID') ? Decrypt_Id_Name(sessionStorage.getItem('UniqueUserID'), 'UForUniqueUserID') : '';
    const localStoreData = useSelector((state) => state.Agency.localStoreData);

    const useQuery = () => {
        const params = new URLSearchParams(location.search);
        return {
            get: (param) => params.get(param)
        };
    };

    const query = useQuery();
    const AgyName = query?.get("AgyName");
    const ORINum = query?.get("ORINum");
    const AId = query?.get("Aid");
    const AgySta = query?.get("ASta");
    const AgencyPage = query?.get("AgencyPage");
    const PersonnelPage = query?.get("PersonnelPage");
    const perId = query?.get("perId");
    const subTab = query?.get("subTab");
    const status = AgySta === true || AgySta === 'true';
    
    // Check if personnel record is selected
    const hasPersonnelRecord = perId && perId !== '0';

    // Sidebar expand/collapse state - start with all menus collapsed
    const [expandList, setExpandList] = useState(null);
    const [plusMinus, setPlusMinus] = useState({
        'Groups & Permission': false,
        'Personnel': false,
        'Security': false,
        'Agency Settings': false,
        'Work Flow & Rules': false,
        'Communication': false,
    });
    
    // Ref to cancel any pending delayed navigation when user clicks elsewhere
    const pendingTimeoutRef = useRef(null);

    // Current page from URL - check if we're on personnelTab or agencyTab
    const currentPage = location.pathname === '/personnelTab' 
        ? (PersonnelPage || 'personnel-home') 
        : (AgencyPage || 'home');

    // Helper function to check if any sub-tab of a main tab is active
    const isMainTabActive = (subTabs) => {
        return subTabs.some(tab => currentPage === tab);
    };

    // Helper function to get default sub-tab for a main tab
    const getDefaultSubTab = (subTabs) => {
        return subTabs[0] || null;
    };

    // Check if main tabs have active sub-tabs
    const isGroupsPermissionActive = isMainTabActive(['Group', 'member', 'screenpermission']);
    const isPersonnelActive = isMainTabActive(['personnel-home', 'personnel-group', 'emergency-contact', 'effective-screen-permission', 'training-cjis', 'certification', 'personnel-audit-log']);
    const isSecurityActive = isMainTabActive(['PasswordSetting']);
    const isAgencySettingsActive = isMainTabActive(['AgencySetting']);
    const isWorkFlowRulesActive = isMainTabActive(['report-workFlow', 'report-workFlow-new', 'Training', 'LegalOrderTemplate', 'ReportTemplate']);
    const isCommunicationActive = isMainTabActive(['AgencyContact', 'NotificationManagement']);

    useEffect(() => {
        if (!localStoreData?.AgencyID || !localStoreData?.PINID) {
            if (uniqueId) dispatch(get_LocalStoreData(uniqueId));
        }
    }, []);

    useEffect(() => {
        if (localStoreData) {
            if (localStoreData.AgencyID && localStoreData.PINID) {
                if (agencyFilterData.length === 0) { getAgency(localStoreData?.AgencyID, localStoreData?.PINID) }
            }
        }
    }, [localStoreData]);

    // Cleanup pending timeouts on unmount
    useEffect(() => {
        return () => {
            if (pendingTimeoutRef.current) {
                clearTimeout(pendingTimeoutRef.current);
            }
        };
    }, []);

    // Handle sidebar menu expand/collapse
    const handleMenuToggle = (menuName) => {
        // Clear any pending timeout
        if (pendingTimeoutRef.current) {
            clearTimeout(pendingTimeoutRef.current);
            pendingTimeoutRef.current = null;
        }
        
        setExpandList(expandList === menuName ? null : menuName);
        setPlusMinus(prevState => ({
            ...prevState,
            [menuName]: !prevState[menuName],
            ...Object.keys(prevState).reduce((acc, key) => {
                if (key !== menuName) acc[key] = false;
                return acc;
            }, {}),
        }));
    };

    // Helper function to preserve all URL parameters
    const preserveParams = () => {
        const params = new URLSearchParams(location.search);
        // Preserve all existing parameters
        const existingParams = {
            Aid: params.get('Aid') || AId || '',
            ASta: params.get('ASta') || AgySta || '',
            AgyName: params.get('AgyName') || AgyName || '',
            ORINum: params.get('ORINum') || ORINum || '',
            perId: params.get('perId') || '',
            perSta: params.get('perSta') || ''
        };
        
        const newParams = new URLSearchParams();
        Object.entries(existingParams).forEach(([key, value]) => {
            if (value) newParams.set(key, value);
        });
        return newParams;
    };

    // Handle page navigation (allow switching tabs even when there are unsaved changes)
    const handlePageChange = (page, subTab = null, ignoreChanges = false) => {
        const params = preserveParams();
        
        // Check if this is a personnel page
        const isPersonnelPage = page.startsWith('personnel-');
        
        // Check if we're on personnelTab route
        if (location.pathname === '/personnelTab' && isPersonnelPage) {
            params.set('PersonnelPage', page);
            navigate(`/personnelTab?${params.toString()}`, { replace: true });
        } else {
            // Always navigate to agencyTab route for agency pages
            params.set('AgencyPage', page);
            
            // Add subTab parameter for Agency Settings
            if (subTab) {
                params.set('subTab', subTab);
            } else {
                params.delete('subTab');
            }
            
            navigate(`/agencyTab?${params.toString()}`, { replace: true });
        }
    };

    // Handle Personnel page navigation (specific to personnelTab)
    const handlePersonnelPageChange = (page) => {
        const params = preserveParams();
        params.set('PersonnelPage', page);
        navigate(`/personnelTab?${params.toString()}`, { replace: true });
    };

    return (
        <div style={{ backgroundColor: '#f5f5f5', padding: '10px' }}>
            {/* Agency Header */}
            <div className='agency-sidebar mb-3'>
                <i className="fa fa-chevron-right " style={{ fontSize: '14px' }}></i>
                <span className="ml-2" >
                    ORI - {ORINum || ""}
                    <p className='agency-name-sidebar'>{AgyName || ''}</p>
                </span>
            </div>

            {/* Menu Items */}
            <ul className="metismenu" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {/* Agency - Home */}
                <li className={`${currentPage === 'home' ? 'active' : ''}`} style={{ marginBottom: '5px' }}>
                    <Link
                        to="#"
                        className="d-flex align-items-center p-2"
                        style={{
                            textDecoration: 'none',
                            borderRadius: '4px',
                            cursor: !status ? 'not-allowed' : 'pointer',
                            backgroundColor: currentPage === 'home' ? '#001e3f' : 'transparent',
                            color: currentPage === 'home' ? '#ffffff' : (!status ? '#999999' : '#333333'),
                            padding: '8px 12px'
                        }}
                        onClick={(e) => {
                            e.preventDefault();
                            if (status) {
                                // Clear any pending timeout
                                if (pendingTimeoutRef.current) {
                                    clearTimeout(pendingTimeoutRef.current);
                                    pendingTimeoutRef.current = null;
                                }
                                
                                // Close all expandable menus
                                setExpandList(null);
                                setPlusMinus({
                                    'Groups & Permission': false,
                                    'Personnel': false,
                                    'Security': false,
                                    'Agency Settings': false,
                                    'Work Flow & Rules': false,
                                    'Communication': false,
                                });
                                
                                handlePageChange('home');
                            }
                        }}
                    >
                        <FontAwesomeIcon icon={faHome} className="react-icon" />
                        <span className="ml-2">Agency Home</span>
                    </Link>
                </li>

                {/* Groups & Permission */}
                <li style={{ marginBottom: '5px' }}>
                    <Link
                        to="#"
                        className="has-arrow arrow-c d-flex align-items-center p-2"
                        style={{
                            textDecoration: 'none',
                            borderRadius: '4px',
                            cursor: !status ? 'not-allowed' : 'pointer',
                            backgroundColor: 'transparent',
                            color: !status ? '#999999' : '#333333',
                            padding: '8px 12px'
                        }}
                        aria-expanded={plusMinus['Groups & Permission']}
                        onClick={(e) => {
                            e.preventDefault();
                            if (status) {
                                if (pendingTimeoutRef.current) {
                                    clearTimeout(pendingTimeoutRef.current);
                                    pendingTimeoutRef.current = null;
                                }
                                const isCurrentlyExpanded = expandList === 'Groups & Permission';
                                handleMenuToggle('Groups & Permission');
                                if (!isCurrentlyExpanded && !isGroupsPermissionActive) {
                                    handlePageChange('Group');
                                }
                            }
                        }}
                    >
                        <FontAwesomeIcon icon={faUsers} className="react-icon" />
                        <span className="ml-2">Groups & Permissions</span>
                    </Link>
                    <ul
                        id="menu"
                        role="menu"
                        className={`${expandList === 'Groups & Permission' ? 'collapse in' : 'collapse'}`}
                        style={{ 
                            marginLeft: '0px', 
                            listStyle: 'none', 
                            paddingLeft: '20px',
                            marginTop: '4px'
                        }}
                    >
                        <li className="ml-2 p-0" style={{ marginBottom: '4px' }}>
                            <Link
                                to="#"
                                className="d-flex align-items-center p-2"
                                style={{ 
                                    textDecoration: 'none', 
                                    borderRadius: '4px', 
                                    fontSize: '14px',
                                    backgroundColor: currentPage === 'Group' ? '#001e3f' : '#e9ecef',
                                    color: currentPage === 'Group' ? '#ffffff' : '#333333',
                                    padding: '6px 12px'
                                }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handlePageChange('Group');
                                }}
                            >
                                <FontAwesomeIcon icon={faUserShield} className="react-icon pl-1" />
                                <span className="m-0 p-0 pl-3">
                                    Groups{count?.GroupCount > 0 && status ? `(${count.GroupCount})` : ''}
                                </span>
                            </Link>
                        </li>
                        <li className="ml-2 p-0" style={{ marginBottom: '4px' }}>
                            <Link
                                to="#"
                                className="d-flex align-items-center p-2"
                                style={{ 
                                    textDecoration: 'none', 
                                    borderRadius: '4px', 
                                    fontSize: '14px',
                                    backgroundColor: currentPage === 'member' ? '#001e3f' : '#e9ecef',
                                    color: currentPage === 'member' ? '#ffffff' : '#333333',
                                    padding: '6px 12px',
                                    whiteSpace: 'nowrap'
                                }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handlePageChange('member');
                                }}
                            >
                                <FontAwesomeIcon icon={faUsers} className="react-icon pl-1" />
                                <span className="m-0 p-0 pl-3">
                                    Group Membership{count?.member > 0 && status ? `(${count.member})` : ''}
                                </span>
                            </Link>
                        </li>
                        <li className="ml-2 p-0" style={{ marginBottom: '4px' }}>
                            <Link
                                to="#"
                                className="d-flex align-items-center p-2"
                                style={{ 
                                    textDecoration: 'none', 
                                    borderRadius: '4px', 
                                    fontSize: '14px',
                                    backgroundColor: currentPage === 'screenpermission' ? '#001e3f' : '#e9ecef',
                                    color: currentPage === 'screenpermission' ? '#ffffff' : '#333333',
                                    padding: '6px 12px'
                                }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handlePageChange('screenpermission');
                                }}
                            >
                                <FontAwesomeIcon icon={faLock} className="react-icon pl-1" />
                                <span className="m-0 p-0 pl-3">Screen Permissions</span>
                            </Link>
                        </li>
                    </ul>
                </li>

                {/* Personnel */}
                <li style={{ marginBottom: '5px' }}>
                    <Link
                        to="#"
                        className="has-arrow arrow-c d-flex align-items-center p-2"
                        style={{
                            textDecoration: 'none',
                            borderRadius: '4px',
                            cursor: !status ? 'not-allowed' : 'pointer',
                            backgroundColor: 'transparent',
                            color: !status ? '#999999' : '#333333',
                            padding: '8px 12px'
                        }}
                        aria-expanded={plusMinus['Personnel']}
                        onClick={(e) => {
                            e.preventDefault();
                            if (status) {
                                if (pendingTimeoutRef.current) {
                                    clearTimeout(pendingTimeoutRef.current);
                                    pendingTimeoutRef.current = null;
                                }
                                const isCurrentlyExpanded = expandList === 'Personnel';
                                handleMenuToggle('Personnel');
                                if (!isCurrentlyExpanded && !isPersonnelActive) {
                                    handlePersonnelPageChange('personnel-home');
                                }
                            }
                        }}
                    >
                        <FontAwesomeIcon icon={faUser} className="react-icon" />
                        <span className="ml-2">Personnel</span>
                    </Link>
                    <ul
                        id="menu"
                        role="menu"
                        className={`${expandList === 'Personnel' ? 'collapse in' : 'collapse'}`}
                        style={{ 
                            marginLeft: '0px', 
                            listStyle: 'none', 
                            paddingLeft: '20px',
                            marginTop: '4px'
                        }}
                    >
                        <li className="ml-2 p-0" style={{ marginBottom: '4px' }}>
                            <Link
                                to="#"
                                className="d-flex align-items-center p-2"
                                style={{ 
                                    textDecoration: 'none', 
                                    borderRadius: '4px', 
                                    fontSize: '14px',
                                    backgroundColor: currentPage === 'personnel-home' ? '#001e3f' : '#e9ecef',
                                    color: currentPage === 'personnel-home' ? '#ffffff' : '#333333',
                                    padding: '6px 12px',
                                    cursor: 'pointer'
                                }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handlePersonnelPageChange('personnel-home');
                                }}
                            >
                                <FontAwesomeIcon icon={faHome} className="react-icon pl-1" style={{ fontSize: '14px' }} />
                                <span className="m-0 p-0 pl-3">Home</span>
                            </Link>
                        </li>
                        <li className="ml-2 p-0" style={{ marginBottom: '4px' }}>
                            <Link
                                to="#"
                                className="d-flex align-items-center p-2"
                                style={{ 
                                    textDecoration: 'none', 
                                    borderRadius: '4px', 
                                    fontSize: '14px',
                                    backgroundColor: currentPage === 'personnel-group' ? '#001e3f' : '#e9ecef',
                                    color: currentPage === 'personnel-group' ? '#ffffff' : (hasPersonnelRecord ? '#333333' : '#999999'),
                                    padding: '6px 12px',
                                    cursor: hasPersonnelRecord ? 'pointer' : 'not-allowed'
                                }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (hasPersonnelRecord) {
                                        handlePersonnelPageChange('personnel-group');
                                    }
                                }}
                            >
                                <FontAwesomeIcon icon={faUserShield} className="react-icon pl-1" style={{ fontSize: '14px' }} />
                                <span className="m-0 p-0 pl-3">Groups</span>
                            </Link>
                        </li>
                        <li className="ml-2 p-0" style={{ marginBottom: '4px' }}>
                            <Link
                                to="#"
                                className="d-flex align-items-center p-2"
                                style={{ 
                                    textDecoration: 'none', 
                                    borderRadius: '4px', 
                                    fontSize: '14px',
                                    backgroundColor: currentPage === 'emergency-contact' ? '#001e3f' : '#e9ecef',
                                    color: currentPage === 'emergency-contact' ? '#ffffff' : (hasPersonnelRecord ? '#333333' : '#999999'),
                                    padding: '6px 12px',
                                    cursor: hasPersonnelRecord ? 'pointer' : 'not-allowed'
                                }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (hasPersonnelRecord) {
                                        handlePersonnelPageChange('emergency-contact');
                                    }
                                }}
                            >
                                <FontAwesomeIcon icon={faAddressCard} className="react-icon pl-1" style={{ fontSize: '14px' }} />
                                <span className="m-0 p-0 pl-3">Emergency Contact</span>
                            </Link>
                        </li>
                        <li className="ml-2 p-0" style={{ marginBottom: '4px' }}>
                            <Link
                                to="#"
                                className="d-flex align-items-center p-2"
                                style={{ 
                                    textDecoration: 'none', 
                                    borderRadius: '4px', 
                                    fontSize: '14px',
                                    backgroundColor: currentPage === 'effective-screen-permission' ? '#001e3f' : '#e9ecef',
                                    color: currentPage === 'effective-screen-permission' ? '#ffffff' : (hasPersonnelRecord ? '#333333' : '#999999'),
                                    padding: '6px 12px',
                                    cursor: hasPersonnelRecord ? 'pointer' : 'not-allowed'
                                }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (hasPersonnelRecord) {
                                        handlePersonnelPageChange('effective-screen-permission');
                                    }
                                }}
                            >
                                <FontAwesomeIcon icon={faLock} className="react-icon pl-1" style={{ fontSize: '14px' }} />
                                <span className="m-0 p-0 pl-3">Effective Screen Permissions</span>
                            </Link>
                        </li>
                        <li className="ml-2 p-0" style={{ marginBottom: '4px' }}>
                            <Link
                                to="#"
                                className="d-flex align-items-center p-2"
                                style={{ 
                                    textDecoration: 'none', 
                                    borderRadius: '4px', 
                                    fontSize: '14px',
                                    backgroundColor: currentPage === 'training-cjis' ? '#001e3f' : '#e9ecef',
                                    color: currentPage === 'training-cjis' ? '#ffffff' : (hasPersonnelRecord ? '#333333' : '#999999'),
                                    padding: '6px 12px',
                                    cursor: hasPersonnelRecord ? 'pointer' : 'not-allowed'
                                }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (hasPersonnelRecord) {
                                        handlePersonnelPageChange('training-cjis');
                                    }
                                }}
                            >
                                <FontAwesomeIcon icon={faGraduationCap} className="react-icon pl-1" style={{ fontSize: '14px' }} />
                                <span className="m-0 p-0 pl-3">Training & CJIS Compliance</span>
                            </Link>
                        </li>
                        <li className="ml-2 p-0" style={{ marginBottom: '4px' }}>
                            <Link
                                to="#"
                                className="d-flex align-items-center p-2"
                                style={{ 
                                    textDecoration: 'none', 
                                    borderRadius: '4px', 
                                    fontSize: '14px',
                                    backgroundColor: currentPage === 'certification' ? '#001e3f' : '#e9ecef',
                                    color: currentPage === 'certification' ? '#ffffff' : (hasPersonnelRecord ? '#333333' : '#999999'),
                                    padding: '6px 12px',
                                    cursor: hasPersonnelRecord ? 'pointer' : 'not-allowed'
                                }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (hasPersonnelRecord) {
                                        handlePersonnelPageChange('certification');
                                    }
                                }}
                            >
                                <FontAwesomeIcon icon={faCertificate} className="react-icon pl-1" style={{ fontSize: '14px' }} />
                                <span className="m-0 p-0 pl-3">Certifications</span>
                            </Link>
                        </li>
                        <li className="ml-2 p-0" style={{ marginBottom: '4px' }}>
                            <Link
                                to="#"
                                className="d-flex align-items-center p-2"
                                style={{ 
                                    textDecoration: 'none', 
                                    borderRadius: '4px', 
                                    fontSize: '14px',
                                    backgroundColor: currentPage === 'personnel-audit-log' ? '#001e3f' : '#e9ecef',
                                    color: currentPage === 'personnel-audit-log' ? '#ffffff' : (hasPersonnelRecord ? '#333333' : '#999999'),
                                    padding: '6px 12px',
                                    cursor: hasPersonnelRecord ? 'pointer' : 'not-allowed'
                                }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (hasPersonnelRecord) {
                                        handlePersonnelPageChange('personnel-audit-log');
                                    }
                                }}
                            >
                                <FontAwesomeIcon icon={faHistory} className="react-icon pl-1" style={{ fontSize: '14px' }} />
                                <span className="m-0 p-0 pl-3">Audit Log</span>
                            </Link>
                        </li>
                    </ul>
                </li>

                {/* Security */}
                <li style={{ marginBottom: '5px' }}>
                    <Link
                        to="#"
                        className="d-flex align-items-center p-2"
                        style={{
                            textDecoration: 'none',
                            borderRadius: '4px',
                            cursor: !status ? 'not-allowed' : 'pointer',
                            backgroundColor: currentPage === 'PasswordSetting' ? '#001e3f' : 'transparent',
                            color: currentPage === 'PasswordSetting' ? '#ffffff' : (!status ? '#999999' : '#333333'),
                            padding: '8px 12px'
                        }}
                        onClick={(e) => {
                            e.preventDefault();
                            if (status) {
                                // Clear any pending timeout
                                if (pendingTimeoutRef.current) {
                                    clearTimeout(pendingTimeoutRef.current);
                                    pendingTimeoutRef.current = null;
                                }
                                
                                // Close all expandable menus
                                setExpandList(null);
                                setPlusMinus({
                                    'Groups & Permission': false,
                                    'Personnel': false,
                                    'Security': false,
                                    'Agency Settings': false,
                                    'Work Flow & Rules': false,
                                    'Communication': false,
                                });
                                
                                handlePageChange('PasswordSetting');
                            }
                        }}
                    >
                        <FontAwesomeIcon icon={faLock} className="react-icon" />
                        <span className="ml-2">Password Settings</span>
                    </Link>
                </li>

                {/* Agency Settings */}
                <li style={{ marginBottom: '5px' }}>
                    <Link
                        to="#"
                        className="has-arrow arrow-c d-flex align-items-center p-2"
                        style={{
                            textDecoration: 'none',
                            borderRadius: '4px',
                            cursor: !status ? 'not-allowed' : 'pointer',
                            backgroundColor: 'transparent',
                            color: !status ? '#999999' : '#333333',
                            padding: '8px 12px'
                        }}
                        aria-expanded={plusMinus['Agency Settings']}
                        onClick={(e) => {
                            e.preventDefault();
                            if (status) {
                                if (pendingTimeoutRef.current) {
                                    clearTimeout(pendingTimeoutRef.current);
                                    pendingTimeoutRef.current = null;
                                }
                                const isCurrentlyExpanded = expandList === 'Agency Settings';
                                handleMenuToggle('Agency Settings');
                                if (!isCurrentlyExpanded && !isAgencySettingsActive) {
                                    handlePageChange('AgencySetting', 'agencyIdentity');
                                }
                            }
                        }}
                    >
                        <FontAwesomeIcon icon={faCog} className="react-icon" />
                        <span className="ml-2">Agency Settings</span>
                    </Link>
                    <ul
                        id="menu"
                        role="menu"
                        className={`${expandList === 'Agency Settings' ? 'collapse in' : 'collapse'}`}
                        style={{ 
                            marginLeft: '0px', 
                            listStyle: 'none', 
                            paddingLeft: '20px',
                            marginTop: '4px'
                        }}
                    >
                        <li className="ml-2 p-0" style={{ marginBottom: '4px' }}>
                            <Link
                                to="#"
                                className="d-flex align-items-center p-2"
                                style={{ 
                                    textDecoration: 'none', 
                                    borderRadius: '4px', 
                                    fontSize: '14px',
                                    backgroundColor: (currentPage === 'AgencySetting' && subTab === 'agencyIdentity') ? '#001e3f' : '#e9ecef',
                                    color: (currentPage === 'AgencySetting' && subTab === 'agencyIdentity') ? '#ffffff' : '#333333',
                                    padding: '6px 12px'
                                }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handlePageChange('AgencySetting', 'agencyIdentity', true);
                                }}
                            >
                                <FontAwesomeIcon icon={faCog} className="react-icon pl-1" style={{ fontSize: '14px' }} />
                                <span className="m-0 p-0 pl-3">Agency Identity & Jurisdiction</span>
                            </Link>
                        </li>
                        <li className="ml-2 p-0" style={{ marginBottom: '4px' }}>
                            <Link
                                to="#"
                                className="d-flex align-items-center p-2"
                                style={{ 
                                    textDecoration: 'none', 
                                    borderRadius: '4px', 
                                    fontSize: '14px',
                                    backgroundColor: (currentPage === 'AgencySetting' && subTab === 'juvenileAgeRules') ? '#001e3f' : '#e9ecef',
                                    color: (currentPage === 'AgencySetting' && subTab === 'juvenileAgeRules') ? '#ffffff' : '#333333',
                                    padding: '6px 12px'
                                }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handlePageChange('AgencySetting', 'juvenileAgeRules', true);
                                }}
                            >
                                <FontAwesomeIcon icon={faUser} className="react-icon pl-1" style={{ fontSize: '14px' }} />
                                <span className="m-0 p-0 pl-3">Juvenile & Age Rules</span>
                            </Link>
                        </li>
                        <li className="ml-2 p-0" style={{ marginBottom: '4px' }}>
                            <Link
                                to="#"
                                className="d-flex align-items-center p-2"
                                style={{ 
                                    textDecoration: 'none', 
                                    borderRadius: '4px', 
                                    fontSize: '14px',
                                    backgroundColor: (currentPage === 'AgencySetting' && subTab === 'recordsRetention') ? '#001e3f' : '#e9ecef',
                                    color: (currentPage === 'AgencySetting' && subTab === 'recordsRetention') ? '#ffffff' : '#333333',
                                    padding: '6px 12px'
                                }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handlePageChange('AgencySetting', 'recordsRetention', true);
                                }}
                            >
                                <FontAwesomeIcon icon={faHistory} className="react-icon pl-1" style={{ fontSize: '14px' }} />
                                <span className="m-0 p-0 pl-3">Records Retention & Destruction</span>
                            </Link>
                        </li>
                        <li className="ml-2 p-0" style={{ marginBottom: '4px' }}>
                            <Link
                                to="#"
                                className="d-flex align-items-center p-2"
                                style={{ 
                                    textDecoration: 'none', 
                                    borderRadius: '4px', 
                                    fontSize: '14px',
                                    backgroundColor: (currentPage === 'AgencySetting' && subTab === 'crossAgencySearch') ? '#001e3f' : '#e9ecef',
                                    color: (currentPage === 'AgencySetting' && subTab === 'crossAgencySearch') ? '#ffffff' : '#333333',
                                    padding: '6px 12px'
                                }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handlePageChange('AgencySetting', 'crossAgencySearch', true);
                                }}
                            >
                                <FontAwesomeIcon icon={faShieldAlt} className="react-icon pl-1" style={{ fontSize: '14px' }} />
                                <span className="m-0 p-0 pl-3">Cross-Agency Search & Visibility</span>
                            </Link>
                        </li>
                        <li className="ml-2 p-0" style={{ marginBottom: '4px' }}>
                            <Link
                                to="#"
                                className="d-flex align-items-center p-2"
                                style={{ 
                                    textDecoration: 'none', 
                                    borderRadius: '4px', 
                                    fontSize: '14px',
                                    backgroundColor: (currentPage === 'AgencySetting' && subTab === 'redactionPolicy') ? '#001e3f' : '#e9ecef',
                                    color: (currentPage === 'AgencySetting' && subTab === 'redactionPolicy') ? '#ffffff' : '#333333',
                                    padding: '6px 12px'
                                }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handlePageChange('AgencySetting', 'redactionPolicy', true);
                                }}
                            >
                                <FontAwesomeIcon icon={faLock} className="react-icon pl-1" style={{ fontSize: '14px' }} />
                                <span className="m-0 p-0 pl-3">Redaction Policy</span>
                            </Link>
                        </li>
                        <li className="ml-2 p-0" style={{ marginBottom: '4px' }}>
                            <Link
                                to="#"
                                className="d-flex align-items-center p-2"
                                style={{ 
                                    textDecoration: 'none', 
                                    borderRadius: '4px', 
                                    fontSize: '14px',
                                    backgroundColor: (currentPage === 'AgencySetting' && subTab === 'propertyRoomDefaults') ? '#001e3f' : '#e9ecef',
                                    color: (currentPage === 'AgencySetting' && subTab === 'propertyRoomDefaults') ? '#ffffff' : '#333333',
                                    padding: '6px 12px'
                                }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handlePageChange('AgencySetting', 'propertyRoomDefaults', true);
                                }}
                            >
                                <FontAwesomeIcon icon={faHome} className="react-icon pl-1" style={{ fontSize: '14px' }} />
                                <span className="m-0 p-0 pl-3">Property Room Defaults</span>
                            </Link>
                        </li>
                    </ul>
                </li>

                {/* Work Flow & Rules */}
                <li style={{ marginBottom: '5px' }}>
                    <Link
                        to="#"
                        className="has-arrow arrow-c d-flex align-items-center p-2"
                        style={{
                            textDecoration: 'none',
                            borderRadius: '4px',
                            cursor: !status ? 'not-allowed' : 'pointer',
                            backgroundColor: 'transparent',
                            color: !status ? '#999999' : '#333333',
                            padding: '8px 12px'
                        }}
                        aria-expanded={plusMinus['Work Flow & Rules']}
                        onClick={(e) => {
                            e.preventDefault();
                            if (status) {
                                if (pendingTimeoutRef.current) {
                                    clearTimeout(pendingTimeoutRef.current);
                                    pendingTimeoutRef.current = null;
                                }
                                const isCurrentlyExpanded = expandList === 'Work Flow & Rules';
                                handleMenuToggle('Work Flow & Rules');
                                if (!isCurrentlyExpanded && !isWorkFlowRulesActive) {
                                    handlePageChange('report-workFlow');
                                }
                            }
                        }}
                    >
                        <FontAwesomeIcon icon={faSitemap} className="react-icon" />
                        <span className="ml-2">Work Flow & Rules</span>
                    </Link>
                    <ul
                        id="menu"
                        role="menu"
                        className={`${expandList === 'Work Flow & Rules' ? 'collapse in' : 'collapse'}`}
                        style={{ 
                            marginLeft: '0px', 
                            listStyle: 'none', 
                            paddingLeft: '20px',
                            marginTop: '4px'
                        }}
                    >
                        <li className="ml-2 p-0" style={{ marginBottom: '4px' }}>
                            <Link
                                to="#"
                                className="d-flex align-items-center p-2"
                                style={{ 
                                    textDecoration: 'none', 
                                    borderRadius: '4px', 
                                    fontSize: '14px',
                                    backgroundColor: currentPage === 'report-workFlow' ? '#001e3f' : '#e9ecef',
                                    color: currentPage === 'report-workFlow' ? '#ffffff' : '#333333',
                                    padding: '6px 12px'
                                }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handlePageChange('report-workFlow');
                                }}
                            >
                                <FontAwesomeIcon icon={faSitemap} className="react-icon pl-1" style={{ fontSize: '14px' }} />
                                <span className="m-0 p-0 pl-3">Report Workflow</span>
                            </Link>
                            {/* {currentPage === 'report-workFlow' && (
                                <div style={{ marginTop: '8px', marginLeft: '0px' }}>
                                    <ReportWorkflowList />
                                </div>
                            )} */}
                        </li>
                        {/* <li className="ml-2 p-0" style={{ marginBottom: '4px' }}>
                            <Link
                                to="#"
                                className="d-flex align-items-center p-2"
                                style={{ 
                                    textDecoration: 'none', 
                                    borderRadius: '4px', 
                                    fontSize: '14px',
                                    backgroundColor: currentPage === 'report-workFlow-new' ? '#001e3f' : '#e9ecef',
                                    color: currentPage === 'report-workFlow-new' ? '#ffffff' : '#333333',
                                    padding: '6px 12px'
                                }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handlePageChange('report-workFlow-new');
                                }}
                            >
                                <FontAwesomeIcon icon={faSitemap} className="react-icon pl-1" style={{ fontSize: '14px' }} />
                                <span className="m-0 p-0 pl-3">Report Workflow (New)</span>
                            </Link>
                        </li> */}
                        <li className="ml-2 p-0" style={{ marginBottom: '4px' }}>
                            <Link
                                to="#"
                                className="d-flex align-items-center p-2"
                                style={{ 
                                    textDecoration: 'none', 
                                    borderRadius: '4px', 
                                    fontSize: '14px',
                                    backgroundColor: currentPage === 'Training' ? '#001e3f' : '#e9ecef',
                                    color: currentPage === 'Training' ? '#ffffff' : '#333333',
                                    padding: '6px 12px'
                                }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handlePageChange('Training');
                                }}
                            >
                                <FontAwesomeIcon icon={faGraduationCap} className="react-icon pl-1" style={{ fontSize: '14px' }} />
                                <span className="m-0 p-0 pl-3">Training</span>
                            </Link>
                        </li>
                        <li className="ml-2 p-0" style={{ marginBottom: '4px' }}>
                            <Link
                                to="#"
                                className="d-flex align-items-center p-2"
                                style={{ 
                                    textDecoration: 'none', 
                                    borderRadius: '4px', 
                                    fontSize: '14px',
                                    backgroundColor: currentPage === 'LegalOrderTemplate' ? '#001e3f' : '#e9ecef',
                                    color: currentPage === 'LegalOrderTemplate' ? '#ffffff' : '#333333',
                                    padding: '6px 12px'
                                }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handlePageChange('LegalOrderTemplate');
                                }}
                            >
                                <FontAwesomeIcon icon={faFileContract} className="react-icon pl-1" style={{ fontSize: '14px' }} />
                                <span className="m-0 p-0 pl-3">Legal Order Template</span>
                            </Link>
                        </li>
                        <li className="ml-2 p-0" style={{ marginBottom: '4px' }}>
                            <Link
                                to="#"
                                className="d-flex align-items-center p-2"
                                style={{ 
                                    textDecoration: 'none', 
                                    borderRadius: '4px', 
                                    fontSize: '14px',
                                    backgroundColor: currentPage === 'ReportTemplate' ? '#001e3f' : '#e9ecef',
                                    color: currentPage === 'ReportTemplate' ? '#ffffff' : '#333333',
                                    padding: '6px 12px'
                                }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handlePageChange('ReportTemplate');
                                }}
                            >
                                <FontAwesomeIcon icon={faFileInvoice} className="react-icon pl-1" style={{ fontSize: '14px' }} />
                                <span className="m-0 p-0 pl-3">Report Template</span>
                            </Link>
                        </li>
                    </ul>
                </li>

                {/* Communication */}
                <li style={{ marginBottom: '5px' }}>
                    <Link
                        to="#"
                        className="has-arrow arrow-c d-flex align-items-center p-2"
                        style={{
                            textDecoration: 'none',
                            borderRadius: '4px',
                            cursor: !status ? 'not-allowed' : 'pointer',
                            backgroundColor: 'transparent',
                            color: !status ? '#999999' : '#333333',
                            padding: '8px 12px'
                        }}
                        aria-expanded={plusMinus['Communication']}
                        onClick={(e) => {
                            e.preventDefault();
                            if (status) {
                                if (pendingTimeoutRef.current) {
                                    clearTimeout(pendingTimeoutRef.current);
                                    pendingTimeoutRef.current = null;
                                }
                                const isCurrentlyExpanded = expandList === 'Communication';
                                handleMenuToggle('Communication');
                                if (!isCurrentlyExpanded && !isCommunicationActive) {
                                    handlePageChange('AgencyContact');
                                }
                            }
                        }}
                    >
                        <FontAwesomeIcon icon={faComments} className="react-icon" />
                        <span className="ml-2">Communication</span>
                    </Link>
                    <ul
                        id="menu"
                        role="menu"
                        className={`${expandList === 'Communication' ? 'collapse in' : 'collapse'}`}
                        style={{ 
                            marginLeft: '0px', 
                            listStyle: 'none', 
                            paddingLeft: '20px',
                            marginTop: '4px'
                        }}
                    >
                        <li className="ml-2 p-0" style={{ marginBottom: '4px' }}>
                            <Link
                                to="#"
                                className="d-flex align-items-center p-2"
                                style={{ 
                                    textDecoration: 'none', 
                                    borderRadius: '4px', 
                                    fontSize: '14px',
                                    backgroundColor: currentPage === 'AgencyContact' ? '#001e3f' : '#e9ecef',
                                    color: currentPage === 'AgencyContact' ? '#ffffff' : '#333333',
                                    padding: '6px 12px'
                                }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handlePageChange('AgencyContact');
                                }}
                            >
                                <FontAwesomeIcon icon={faAddressCard} className="react-icon pl-1" style={{ fontSize: '14px' }} />
                                <span className="m-0 p-0 pl-3">
                                    Agency Contact{count?.AgencyContactCount > 0 && status ? `(${count.AgencyContactCount})` : ''}
                                </span>
                            </Link>
                        </li>
                        <li className="ml-2 p-0" style={{ marginBottom: '4px' }}>
                            <Link
                                to="#"
                                className="d-flex align-items-center p-2"
                                style={{ 
                                    textDecoration: 'none', 
                                    borderRadius: '4px', 
                                    fontSize: '14px',
                                    backgroundColor: currentPage === 'NotificationManagement' ? '#001e3f' : '#e9ecef',
                                    color: currentPage === 'NotificationManagement' ? '#ffffff' : '#333333',
                                    padding: '6px 12px'
                                }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handlePageChange('NotificationManagement');
                                }}
                            >
                                <FontAwesomeIcon icon={faBell} className="react-icon pl-1" style={{ fontSize: '14px' }} />
                                <span className="m-0 p-0 pl-3">Notification Management</span>
                            </Link>
                        </li>
                    </ul>
                </li>

                {/* Audit Log */}
                <li className={`${currentPage === 'AuditLog' ? 'active' : ''}`} style={{ marginBottom: '5px' }}>
                    <Link
                        to="#"
                        className="d-flex align-items-center p-2"
                        style={{
                            textDecoration: 'none',
                            borderRadius: '4px',
                            cursor: !status ? 'not-allowed' : 'pointer',
                            backgroundColor: currentPage === 'AuditLog' ? '#001e3f' : 'transparent',
                            color: currentPage === 'AuditLog' ? '#ffffff' : (!status ? '#999999' : '#333333'),
                            padding: '8px 12px'
                        }}
                        onClick={(e) => {
                            e.preventDefault();
                            if (status) {
                                // Clear any pending timeout
                                if (pendingTimeoutRef.current) {
                                    clearTimeout(pendingTimeoutRef.current);
                                    pendingTimeoutRef.current = null;
                                }
                                
                                // Close all expandable menus
                                setExpandList(null);
                                setPlusMinus({
                                    'Groups & Permission': false,
                                    'Personnel': false,
                                    'Security': false,
                                    'Agency Settings': false,
                                    'Work Flow & Rules': false,
                                    'Communication': false,
                                });
                                
                                handlePageChange('AuditLog');
                            }
                        }}
                    >
                        <FontAwesomeIcon icon={faHistory} className="react-icon" />
                        <span className="ml-2">Audit Log</span>
                    </Link>
                </li>
            </ul>
        </div>
    )
}

export default AgencySidebar
